import React from 'react';
import { 
  Search, 
  Filter, 
  RotateCcw, 
  MapPin, 
  User, 
  Activity, 
  Stethoscope, 
  Thermometer
} from 'lucide-react';
import { 
  REGIONS, 
  AGE_GROUPS, 
  BMI_CATEGORIES 
} from '@/lib/index';
import { FilterState } from '@/hooks/useHealthcareData';
import { Input } from '@/components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface FilterProps {
  onFiltersChange: (filters: Partial<FilterState>) => void;
  activeFilters: FilterState;
}

/**
 * FilterPanel provides a comprehensive container for all healthcare data filters
 */
export function FilterPanel({ onFiltersChange, activeFilters }: FilterProps) {
  const activeCount = Object.entries(activeFilters).reduce((acc, [key, value]) => {
    if (key === 'search') return acc;
    return value !== 'all' ? acc + 1 : acc;
  }, 0);

  const resetAll = () => {
    onFiltersChange({
      region: 'all',
      ageGroup: 'all',
      bmiCategory: 'all',
      smoker: 'all',
      treatmentType: 'all',
      status: 'all',
      search: '',
    });
  };

  return (
    <div className="w-full space-y-4 bg-card/50 backdrop-blur-md border border-border/50 p-6 rounded-2xl shadow-sm">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Filter className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Data Exploration</h3>
            <p className="text-xs text-muted-foreground">Analyze specific patient cohorts and trends</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {activeCount > 0 && (
            <Badge variant="secondary" className="h-8 px-3 font-medium text-xs bg-primary/5 text-primary border-primary/20">
              {activeCount} active filters
            </Badge>
          )}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={resetAll}
            className="text-xs hover:bg-destructive/10 hover:text-destructive transition-colors"
          >
            <RotateCcw className="w-3 h-3 mr-2" />
            Reset All
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-2">
        <div className="space-y-2">
          <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
            <Search className="w-3 h-3" /> Search Patient
          </Label>
          <div className="relative">
            <Input
              placeholder="Name or ID..."
              value={activeFilters.search}
              onChange={(e) => onFiltersChange({ search: e.target.value })}
              className="pl-9 bg-background/50 border-border/50 focus:ring-primary/20"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
            <MapPin className="w-3 h-3" /> Geographic Region
          </Label>
          <Select 
            value={activeFilters.region} 
            onValueChange={(val) => onFiltersChange({ region: val })}
          >
            <SelectTrigger className="bg-background/50 border-border/50">
              <SelectValue placeholder="All Regions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Regions</SelectItem>
              {REGIONS.map((reg) => (
                <SelectItem key={reg} value={reg}>
                  {reg.charAt(0).toUpperCase() + reg.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
            <User className="w-3 h-3" /> Age Cohort
          </Label>
          <Select 
            value={activeFilters.ageGroup} 
            onValueChange={(val) => onFiltersChange({ ageGroup: val })}
          >
            <SelectTrigger className="bg-background/50 border-border/50">
              <SelectValue placeholder="All Ages" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Ages</SelectItem>
              {AGE_GROUPS.map((group) => (
                <SelectItem key={group} value={group}>{group}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
            <Activity className="w-3 h-3" /> BMI Category
          </Label>
          <Select 
            value={activeFilters.bmiCategory} 
            onValueChange={(val) => onFiltersChange({ bmiCategory: val })}
          >
            <SelectTrigger className="bg-background/50 border-border/50">
              <SelectValue placeholder="All BMI Ranges" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All BMI Ranges</SelectItem>
              {BMI_CATEGORIES.map((cat) => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-border/30 pt-4">
        <div className="space-y-2">
          <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
             Lifestyle Factor: Smoking
          </Label>
          <div className="flex gap-2">
            {['all', 'yes', 'no'].map((status) => (
              <Button
                key={status}
                variant={activeFilters.smoker === status ? 'default' : 'outline'}
                size="sm"
                className="flex-1 text-xs"
                onClick={() => onFiltersChange({ smoker: status as any })}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
            <Stethoscope className="w-3 h-3" /> Treatment Plan
          </Label>
          <Select 
            value={activeFilters.treatmentType} 
            onValueChange={(val) => onFiltersChange({ treatmentType: val })}
          >
            <SelectTrigger className="bg-background/50 border-border/50">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="Inpatient">Inpatient</SelectItem>
              <SelectItem value="Outpatient">Outpatient</SelectItem>
              <SelectItem value="Emergency">Emergency</SelectItem>
              <SelectItem value="Routine">Routine</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
            <Thermometer className="w-3 h-3" /> Clinical Status
          </Label>
          <Select 
            value={activeFilters.status} 
            onValueChange={(val) => onFiltersChange({ status: val })}
          >
            <SelectTrigger className="bg-background/50 border-border/50">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="Stable">Stable</SelectItem>
              <SelectItem value="Critical">Critical</SelectItem>
              <SelectItem value="Recovering">Recovering</SelectItem>
              <SelectItem value="Follow-up">Follow-up</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

/**
 * FilterControls provides a compact inline version of the filters for smaller UI spaces
 */
export function FilterControls({ onFiltersChange, activeFilters }: FilterProps) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative w-full max-w-sm">
        <Input
          placeholder="Search patients..."
          value={activeFilters.search}
          onChange={(e) => onFiltersChange({ search: e.target.value })}
          className="pl-9 h-10 bg-card border-border/40"
        />
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      </div>

      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="h-10 gap-2 border-border/40">
            <Filter className="w-4 h-4" />
            Filters
            {Object.values(activeFilters).filter(v => v !== 'all' && v !== '').length > 0 && (
              <Badge className="ml-1 px-1.5 h-5 min-w-5 justify-center"> 
                {Object.values(activeFilters).filter(v => v !== 'all' && v !== '').length}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0 shadow-2xl" align="end">
          <div className="p-4 border-b border-border/50">
            <h4 className="font-semibold">Advanced Filtering</h4>
          </div>
          <div className="p-4 space-y-4">
            <div className="space-y-2">
              <Label className="text-xs">Region</Label>
              <Select value={activeFilters.region} onValueChange={(v) => onFiltersChange({ region: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Regions</SelectItem>
                  {REGIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Age Group</Label>
              <Select value={activeFilters.ageGroup} onValueChange={(v) => onFiltersChange({ ageGroup: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ages</SelectItem>
                  {AGE_GROUPS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button 
                variant="ghost" 
                className="flex-1 text-xs" 
                onClick={() => onFiltersChange({ 
                  region: 'all', 
                  ageGroup: 'all', 
                  bmiCategory: 'all', 
                  smoker: 'all', 
                  treatmentType: 'all', 
                  status: 'all', 
                  search: '' 
                })}
              >
                Reset
              </Button>
              <Button className="flex-1 text-xs bg-primary text-primary-foreground">Apply</Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
